import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { icon } from "@fortawesome/fontawesome-svg-core/import.macro";

import TableCard from "../../../components/ui-components/TableCard";
import FlexTable from "../../../components/ui-components/FlexTable";
import EmptyState from "../../../components/ui-components/EmptyState";
import FilterDropdown from "../../../components/ui-components/FilterDropdown";
import { DimensionCell, isFullyHighConfidence } from "./DimensionCell";

const categoryOf = (s) => s.categoryName || "Custom";
const formatCost = (v) => `€${(v || 0).toLocaleString("en-US", { maximumFractionDigits: 0 })}`;

// Source-type icons. The icon() macro is compile-time (string literals only), so each
// candidate icon is resolved at module load and selected by the source's category.
const TYPE_ICONS = {
  compute: icon({ name: "microchip", style: "light" }),
  database: icon({ name: "database", style: "light" }),
  storage: icon({ name: "hard-drive", style: "light" }),
  network: icon({ name: "network-wired", style: "light" }),
  ai: icon({ name: "robot", style: "light" }),
  analytics: icon({ name: "chart-line", style: "light" }),
  project: icon({ name: "folder", style: "light" }),
};
const DEFAULT_TYPE_ICON = icon({ name: "cube", style: "light" });

function sourceTypeIcon(category) {
  const c = (category || "").toLowerCase();
  if (c.includes("compute")) return TYPE_ICONS.compute;
  if (c.includes("managed data") || c.includes("database") || c.includes("data")) return TYPE_ICONS.database;
  if (c.includes("storage")) return TYPE_ICONS.storage;
  if (c.includes("network")) return TYPE_ICONS.network;
  if (c.includes("ai") || c.includes("ml")) return TYPE_ICONS.ai;
  if (c.includes("analytic")) return TYPE_ICONS.analytics;
  if (c.includes("project")) return TYPE_ICONS.project;
  return DEFAULT_TYPE_ICON;
}

// All owner names referenced by a source's rules (primary owner + any split owners).
function ownersOf(s) {
  const names = new Set();
  Object.values(s.cells || {}).forEach((cell) => {
    if (!cell) return;
    if (cell.ownerName) names.add(cell.ownerName);
    (cell.owners || []).forEach((o) => o.ownerName && names.add(o.ownerName));
  });
  return names;
}

// SourceList — the flat (ungrouped) source × dimension table for the /allocation "Sources" tab,
// built on the shared TableCard + FlexTable. The grouped/footer view lives on the "Matrix" tab.
export default function SourceList({ sources = [], dimensions = [], onCellClick = () => {} }) {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [ownerFilter, setOwnerFilter] = useState("");
  const [missingDim, setMissingDim] = useState("");

  // Dropdown option sets, derived from the data.
  const typeOptions = useMemo(
    () => Array.from(new Set(sources.map(categoryOf))).sort(),
    [sources]
  );
  const ownerOptions = useMemo(() => {
    const all = new Set();
    sources.forEach((s) => ownersOf(s).forEach((n) => all.add(n)));
    return Array.from(all).sort();
  }, [sources]);

  const q = search.trim().toLowerCase();
  const visible = useMemo(() => {
    let list = sources;
    if (q) list = list.filter((s) => (s.displayName || "").toLowerCase().includes(q));
    if (typeFilter) list = list.filter((s) => categoryOf(s) === typeFilter);
    if (statusFilter === "needs_attention") list = list.filter((s) => !isFullyHighConfidence(s, dimensions));
    if (statusFilter === "healthy") list = list.filter((s) => isFullyHighConfidence(s, dimensions));
    if (ownerFilter) list = list.filter((s) => ownersOf(s).has(ownerFilter));
    if (missingDim) list = list.filter((s) => !s.cells?.[Number(missingDim)]);
    return [...list].sort((a, b) => (b.monthlyCost || 0) - (a.monthlyCost || 0));
  }, [sources, dimensions, q, typeFilter, statusFilter, ownerFilter, missingDim]);

  // Subtitle: count · total monthly cost · how many need attention (over the visible set).
  const totalCost = useMemo(() => visible.reduce((acc, s) => acc + (s.monthlyCost || 0), 0), [visible]);
  const needAttention = useMemo(
    () => visible.filter((s) => !isFullyHighConfidence(s, dimensions)).length,
    [visible, dimensions]
  );
  const subtitle = `${visible.length} ${visible.length === 1 ? "source" : "sources"} · ${formatCost(totalCost)}/month · ${needAttention} need attention`;

  const columns = useMemo(() => {
    const base = [
      {
        id: "source",
        accessor: "displayName",
        Header: () => <span>Source</span>,
        Cell: ({ row }) => {
          const s = row.original;
          return (
            <div className="flex items-center gap-2.5 min-w-0">
              <FontAwesomeIcon icon={sourceTypeIcon(s.categoryName)} className="shrink-0 text-base text-text-3" />
              <div className="flex flex-col gap-0.5 min-w-0">
                <span className="font-medium text-text truncate">{s.displayName}</span>
                <span className="text-xs text-text-2 truncate">{categoryOf(s)}</span>
              </div>
            </div>
          );
        },
        flex: 1,
        minWidth: 420,
      },
      {
        id: "cost",
        accessor: "monthlyCost",
        align: "right",
        Header: () => <span>Cost/mo</span>,
        Cell: ({ value }) => (
          <span className="tabular-nums text-text">{value != null ? formatCost(value) : "—"}</span>
        ),
        width: 120,
        minWidth: 100,
        maxWidth: 150,
      },
    ];

    const dimCols = dimensions.map((dim) => ({
      id: `dim_${dim.id}`,
      accessor: (row) => row.cells?.[dim.id] ?? null,
      Header: () => <span>{dim.name}</span>,
      Cell: ({ value, row }) => (
        <DimensionCell cell={value} source={row.original} dimensionId={dim.id} onCellClick={onCellClick} />
      ),
      width: 170,
      minWidth: 130,
      maxWidth: 240,
    }));

    const action = {
      id: "action",
      align: "right",
      Header: () => null,
      Cell: ({ row }) => (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/allocation/sources/${row.original.id}`);
          }}
          className="text-sm font-semibold text-primary opacity-0 transition-opacity hover:text-primary-hover group-hover:opacity-100 focus-visible:opacity-100"
        >
          Edit source
        </button>
      ),
      width: 110,
      minWidth: 100,
      maxWidth: 130,
    };

    return [...base, ...dimCols, action];
  }, [dimensions, onCellClick, navigate]);

  const dropdowns = (
    <>
      <FilterDropdown
        ariaLabel="Filter by type"
        value={typeFilter}
        onChange={setTypeFilter}
        minWidth={130}
        options={[{ value: "", label: "All types" }, ...typeOptions.map((t) => ({ value: t, label: t }))]}
      />
      <FilterDropdown
        ariaLabel="Filter by status"
        value={statusFilter}
        onChange={setStatusFilter}
        minWidth={150}
        options={[
          { value: "", label: "All statuses" },
          { value: "needs_attention", label: "Needs attention" },
          { value: "healthy", label: "Healthy" },
        ]}
      />
      <FilterDropdown
        ariaLabel="Filter by owner"
        value={ownerFilter}
        onChange={setOwnerFilter}
        minWidth={140}
        options={[{ value: "", label: "All owners" }, ...ownerOptions.map((o) => ({ value: o, label: o }))]}
      />
      <FilterDropdown
        ariaLabel="Filter by missing field"
        value={missingDim}
        onChange={setMissingDim}
        minWidth={170}
        options={[
          { value: "", label: "Any missing field" },
          ...dimensions.map((d) => ({ value: String(d.id), label: `Missing: ${d.name}` })),
        ]}
      />
    </>
  );

  const filtersActive = q || typeFilter || statusFilter || ownerFilter || missingDim;

  return (
    <TableCard
      title="Sources"
      subtitle={subtitle}
      primaryAction={{
        label: "New source",
        onClick: () => navigate("/allocation/sources/new"),
        icon: icon({ name: "plus", style: "solid" }),
      }}
      searchValue={search}
      onSearchChange={setSearch}
      searchPlaceholder="Search sources…"
      controls={dropdowns}
    >
      {visible.length === 0 ? (
        <EmptyState
          icon={icon({ name: "layer-group", style: "light" })}
          title={filtersActive ? "No matching sources" : "No sources yet"}
          message={filtersActive ? "No sources match the current filters." : "Create a source to start allocating cost."}
          action={
            filtersActive
              ? {
                  label: "Clear filters",
                  onClick: () => {
                    setSearch("");
                    setTypeFilter("");
                    setStatusFilter("");
                    setOwnerFilter("");
                    setMissingDim("");
                  },
                  variant: "secondary",
                }
              : { label: "New source", onClick: () => navigate("/allocation/sources/new"), icon: icon({ name: "plus", style: "solid" }) }
          }
        />
      ) : (
        <FlexTable
          columns={columns}
          data={visible}
          onRowClick={(id) => navigate(`/allocation/sources/${id}`)}
          rowAriaLabel={(s) => `Edit ${s.displayName}`}
        />
      )}
    </TableCard>
  );
}

SourceList.propTypes = {
  sources: PropTypes.array,
  dimensions: PropTypes.array,
  onCellClick: PropTypes.func,
};
